import { TFunction } from 'i18next'
import { z } from 'zod'

import { EMAIL_REGEX } from '@/constants/regex'
import { GuestModel, LockedResident } from '@/models'
import { Building } from '@/reducers/types'

export const VisitorTypeEnum = z.enum(['guest', 'vendor'])
export type VisitorType = z.infer<typeof VisitorTypeEnum>

export const AccessTypeEnum = z.enum(['one-time', 'recurring'])
export type AccessType = z.infer<typeof AccessTypeEnum>

export const PermissionTypeEnum = z.enum(['custom', 'daily', 'weekly', 'monthly', 'always'])
export type PermissionType = z.infer<typeof PermissionTypeEnum>

export const MAX_NOTE_LENGTH = 200

export const makeAddVisitorSchema = (t: TFunction<['visitors']>) =>
  z
    .object({
      accessType: AccessTypeEnum,
      coi: z.instanceof(File).nullable().optional(),
      company: z.string(),
      customAccess: z.boolean(),
      dueDate: z.date().nullable().optional(),
      email: z.string(),
      endOfDay: z.date().nullable().optional(),
      guestCategory: z.string(),
      isNoteOpen: z.boolean(),
      keyAccess: z.boolean(),
      keylessAccess: z.boolean(),
      monthlyPermission: z.string(),
      monthlyRange: z.tuple([z.date().nullable(), z.date().nullable()]),
      name: z.string().min(1, t('visitors:dashboard.addVisitorModal.validation.nameRequired')),
      note: z.string(),
      permissionType: PermissionTypeEnum,
      phone: z.string(),
      pinCode: z.boolean(),
      resident: z.string().nullable(),
      selectedDays: z.array(z.number()),
      startOfDay: z.date().nullable().optional(),
      timeEnd: z.number().nullable().optional(),
      timeStart: z.number().nullable().optional(),
      visitorId: z
        .string()
        .max(10, t('visitors:dashboard.addVisitorModal.validation.visitorIdTooLong')),
      visitorType: VisitorTypeEnum,
    })
    .superRefine((data, ctx) => {
      if (data.visitorType === VisitorTypeEnum.enum.guest && !data.resident) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('visitors:form.residentRequired'),
          path: ['resident'],
        })
      }

      if (data.isNoteOpen && data.note.length > MAX_NOTE_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('visitors:dashboard.addVisitorModal.validation.noteTooLong', {
            maxLength: MAX_NOTE_LENGTH,
          }),
          path: ['note'],
        })
      }

      if (data.email && !EMAIL_REGEX.test(data.email)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('visitors:form.invalidEmailFormat'),
          path: ['email'],
        })
      }

      if (data.keyAccess) {
        if (!data.phone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:dashboard.addVisitorModal.validation.phoneRequiredKeyAccess'),
            path: ['phone'],
          })
        }
        if (!data.email) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:dashboard.addVisitorModal.validation.emailRequiredKeyAccess'),
            path: ['email'],
          })
        }
      }

      if (data.pinCode && !data.email) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('visitors:dashboard.addVisitorModal.validation.emailRequiredPinCode'),
          path: ['email'],
        })
      }

      if (data.accessType === AccessTypeEnum.enum.recurring) {
        // Note: "Always" grants ongoing access, so it has no time window to validate.
        const isAlways = data.permissionType === PermissionTypeEnum.enum.always

        // Note: Keyless Access and PIN code access require an explicit, bounded visit window,
        // so they're only available with the One Time (custom) permission type.
        if (
          data.permissionType !== PermissionTypeEnum.enum.custom &&
          (data.keyAccess || data.pinCode)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'visitors:dashboard.addVisitorModal.validation.customPermissionRequiredForKeyAccess',
            ),
            path: ['permissionType'],
          })
        }

        if (!isAlways && data.timeStart == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:form.startTimeRequired'),
            path: ['timeStart'],
          })
        }
        if (!isAlways && data.timeEnd == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:form.endTimeRequired'),
            path: ['timeEnd'],
          })
        }
        if (data.permissionType === PermissionTypeEnum.enum.custom && !data.dueDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:dashboard.addVisitorModal.validation.dateRequired'),
            path: ['dueDate'],
          })
        }
        if (
          data.permissionType === PermissionTypeEnum.enum.weekly &&
          data.selectedDays.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('visitors:form.selectDaysRequired'),
            path: ['selectedDays'],
          })
        }
        if (data.permissionType === PermissionTypeEnum.enum.monthly && !data.monthlyRange[0]) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t(
              'visitors:dashboard.addVisitorModal.validation.monthlyPermissionDateRequired',
            ),
            path: ['monthlyRange'],
          })
        }
      }
    })

export type AddVisitorFormValues = z.infer<ReturnType<typeof makeAddVisitorSchema>>

export type ResidentOption = {
  label: string
  value: string
}

export type AddVisitorModalProps = {
  defaultVisitorType?: VisitorType
  editingVisitor?: GuestModel | null
  onClose: () => void
  // Note: pre-selects and locks the resident (and, implicitly, the building) - used when creating
  // a guest from a resident's own profile, where both are implied by context and must not be
  // changed.
  lockResident?: LockedResident
  lockBuilding?: Building | null
}
